import { envConfig } from '#constants/envConfig';

/**
 * Отметка «по пушу постучали».
 *
 * До этого воронка уведомлений обрывалась на ответе FCM, а он говорит только «принял в очередь»:
 * открывают ли люди то, что мы им шлём, было неизвестно вообще. Теперь сервер кладёт в ссылку
 * перехода идентификатор расписки, а мы при открытии его возвращаем.
 *
 * Почему именно параметр в ссылке, а не сообщение из service worker'а или нативного слоя: тап
 * уносит `click_action` целиком по всем трём путям — веб (service worker открывает или
 * перенаправляет окно), Android в фоне (Play Services отдаёт данные Capacitor'у) и Android в
 * foreground (тот же адрес становится deep link'ом). Один и тот же код снимает цифру со всех
 * трёх, и ни в service worker, ни в нативный код лезть не пришлось.
 */

/** То же имя, что и на сервере (`PushReceiptService.MARKER_PARAM`). */
export const PUSH_OPEN_PARAM = 'np';

const PUSH_OPEN_PATH = '/notification/api/v1/push/receipt/opened/';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const endpoint = (id: string): string => {
  try {
    return new URL(PUSH_OPEN_PATH + id, envConfig.API_URL).toString();
  } catch {
    // API_URL относительный (локальная разработка) — тогда и путь относительный.
    return PUSH_OPEN_PATH + id;
  }
};

/**
 * Достать метку расписки из строки запроса.
 *
 * Формат проверяем: параметр приезжает из адреса, а адрес человек может отредактировать или
 * переслать. Мусор до сервера доводить незачем.
 */
export const readPushOpenId = (search: string | null | undefined): string | null => {
  if (!search) {
    return null;
  }
  try {
    const id = new URLSearchParams(search).get(PUSH_OPEN_PARAM);
    return id && UUID_PATTERN.test(id) ? id : null;
  } catch {
    return null;
  }
};

/**
 * Сообщить об открытии. Ничего не ждёт и ничего не возвращает: это телеметрия, и она не имеет
 * права ни задержать переход, ни уронить его. `keepalive` — потому что вызов случается ровно в
 * момент навигации, и без него браузер оборвал бы запрос вместе со старым документом.
 */
export const reportPushOpen = (search: string | null | undefined): void => {
  const id = readPushOpenId(search);
  if (!id) {
    return;
  }
  try {
    void fetch(endpoint(id), {
      method: 'POST',
      keepalive: true,
      // Отметка не про личность: расписка уже знает, кому был отправлен пуш. Куки здесь только
      // мешали бы — человек вполне может открыть уведомление разлогиненным.
      credentials: 'omit',
    }).catch(() => undefined);
  } catch {
    // fetch недоступен или запрещён политикой — молчим. Цифра не стоит ни одной ошибки.
  }
};

/** Убрать метку из адреса: внутри приложения ей делать нечего. */
export const stripPushOpenParam = (search: string): string => {
  if (!search || !search.includes(PUSH_OPEN_PARAM)) {
    return search;
  }
  try {
    const params = new URLSearchParams(search);
    params.delete(PUSH_OPEN_PARAM);
    const rest = params.toString();
    return rest ? `?${rest}` : '';
  } catch {
    return search;
  }
};
